import React, { useCallback } from "react";
import { useNavigate } from "react-router";

import { Badge, IconButton, Tooltip } from "@mui/material";
import GroupWork from "@mui/icons-material/GroupWork";
import FileCopy from "@mui/icons-material/FileCopy";
import Delete from "@mui/icons-material/Delete";
import History from "@mui/icons-material/History";
import Link from "@mui/icons-material/Link";
import AttachFile from "@mui/icons-material/AttachFile";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useTranslations } from "@fenetech/translations";
import useWait from "helpers/context/Page/useWait";
import useUserInfo from "helpers/context/User/useUserInfo";
import useMessageBox from "helpers/context/Page/useMessageBox";

import PrintReport from "components/Quotes/QuoteEntry/Header/PrintReport";
import { IQuote, IQuotePermissions, ILockedObject, ILineItem } from "helpers/interfaces";
import { RoleEnum } from "helpers/enums";
import DownloadReport from "./DownloadReport";
import useQuoteActions from "components/Quotes/useQuoteActions";
import useApplicationInfo from "helpers/context/Application/useApplicationInfo";
import QuoteNavigation from "components/Quotes/QuoteNavigation";
import useQuoteAttachments from "../Attachments/useQuoteAttachments";
import useQuoteAttachmentActions from "../Attachments/useQuoteAttachmentActions";

interface IProps {
    quote?: IQuote,
    lineItems?: ILineItem[],
    permissions?: IQuotePermissions,
    lock?: ILockedObject,
    navigateToDashboard: () => void,
    quoteHasRelatedLinks: boolean,
    setRelatedLinksDialogOpen: (value: boolean) => void,
    onSaberisIconClick: () => void
}

const QuoteHeaderActions: React.FC<IProps> = ({ quote, lineItems, permissions, lock, navigateToDashboard, quoteHasRelatedLinks, setRelatedLinksDialogOpen, onSaberisIconClick }: IProps) => {

    const tm = useTranslations();
    const navigate = useNavigate();
    const wait = useWait();
    const user = useUserInfo();
    const quoteActions = useQuoteActions();
    const application = useApplicationInfo();
    const messageBox = useMessageBox();
    const { attachments, isEnabled: quoteAttachmentsEnabled } = useQuoteAttachments();
    const attachmentsActions = useQuoteAttachmentActions();

    const handleCopyAttachmentsConfirmation = useCallback(async (dialogResult?: boolean) => {
        if (quote?.oKey !== undefined) {
            wait.Show(true);
            const newOKey = await quoteActions.CopyQuoteAsync(quote.oKey);

            if (dialogResult === true && newOKey !== undefined && newOKey > 0) {
                await attachmentsActions.CopyAllAttachmentsAsync(quote.oKey, newOKey);
            }
            
            if (newOKey !== undefined && newOKey > 0) {
                navigate(`/quotes/?oKey=${newOKey}`);
            }
        }
    }, [quote, quoteActions, wait, attachmentsActions, navigate])

    const handleCopyConfirmation = useCallback(async (dialogResult?: boolean) => {
        if (dialogResult === true && quote?.oKey !== undefined) {
            if (attachments && attachments.length > 0) {
                messageBox.Show({ message: tm.Get("Copy quote attachments?"), title: tm.Get("Quote Entry"), yesNoPrompt: true, callback: handleCopyAttachmentsConfirmation });
            } else {
                wait.Show(true);
                const newOKey = await quoteActions.CopyQuoteAsync(quote.oKey);
                if (newOKey !== undefined && newOKey > 0) {
                    navigate(`/quotes/?oKey=${newOKey}`);
                }
            }
        }
        wait.Show(false);
    }, [quote, quoteActions, navigate, wait, attachments, messageBox, handleCopyAttachmentsConfirmation, tm]);

    const handleCopyClick = useCallback(async () => {
        if (quote?.oKey !== undefined) {
            messageBox.Show({ message: tm.Get("Are you sure you want to copy this quote?"), title: tm.Get("Quote Entry"), yesNoPrompt: true, callback: handleCopyConfirmation });
        }
    }, [quote, messageBox, handleCopyConfirmation, tm]);

    const handleGlobalOptionsClick = useCallback(() => {
        navigate(QuoteNavigation.GlobalOptionsURL(quote?.oKey ?? 0));
    }, [navigate, quote]);

    const handleQuoteAttachmentsClick = useCallback(() => {
        navigate(QuoteNavigation.QuoteAttachmentsURL(quote?.oKey ?? 0));
    }, [navigate, quote]);

    const handleTransLogClick = useCallback(() => {
        navigate(QuoteNavigation.TransLogURL(quote?.oKey ?? 0));
    }, [navigate, quote]);


    const handleDeleteConfirmation = useCallback(async (dialogResult?: boolean) => {
        if (dialogResult === true && quote?.oKey !== undefined) {
            wait.Show(true);
            const result = await quoteActions.DeleteQuoteAsync(quote.oKey);
            if (result) {
                navigateToDashboard();
            }
            wait.Show(false);
        }
    }, [quote, quoteActions, navigateToDashboard, wait]);

    const handleDeleteClick = useCallback(() => {
        if (quote?.oKey !== undefined) {
            messageBox.Show({ message: tm.Get("Are you sure you want to delete this quote?"), title: tm.Get("Quote Entry"), yesNoPrompt: true, callback: handleDeleteConfirmation });
        }
    }, [quote, messageBox, handleDeleteConfirmation, tm]);

    const userCanModifyQuote = React.useMemo(() => {
        if (permissions && lock) {
            return permissions.editQuote && !lock.isLockedByAnotherUser;
        }
        return false;
    }, [permissions, lock]);

    const userCanCopyQuote = React.useMemo(() => {
        if (permissions) {
            return permissions.copyQuote;
        }
        return false;
    }, [permissions]);

    const userCanViewTransLog = React.useMemo(() => {
        if (permissions && user.HasRole(RoleEnum.CSR) && application.parameters.performOrderLocking) {
            return true;
        }
        return false;
    }, [permissions, user, application]);

    const userCanDeleteQuote = React.useMemo(() => {
        if (permissions && lock) {
            return permissions.deleteQuote && !lock.isLockedByAnotherUser;
        }
        return false;
    }, [permissions, lock]);

    const saberisIconVisible = React.useMemo(() => {
        if (lineItems && permissions) {
            return lineItems.length > 0 && permissions.canAccessSaberis && (user.HasRole(RoleEnum.Dealer) || user.HasRole(RoleEnum.CSR));
        }
        return false;
    }, [user, lineItems, permissions]);

    return <>
        {saberisIconVisible &&
            <Tooltip title={tm.Get("Upload to Saberis")}>
                <IconButton aria-label="saberis" sx={{ padding: 0 }} onClick={onSaberisIconClick}>
                    <FileUploadIcon color="primary" />
                </IconButton>
            </Tooltip>
        }

        {quoteAttachmentsEnabled &&
            <Tooltip title={tm.Get("Attachments")} sx={{marginBottom: attachments.length > 0 ? 1 : 0}}>
                <Badge badgeContent={attachments.length} color="secondary"
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}>
                    <IconButton aria-label="attachments" sx={{ padding: 0 }} onClick={handleQuoteAttachmentsClick}>
                        <AttachFile color="primary" />
                    </IconButton>
                </Badge>
            </Tooltip>
        }

        {quoteHasRelatedLinks &&
            <Tooltip title={tm.Get("Links")}>
                <IconButton aria-label="related links" sx={{ padding: 0 }} onClick={() => setRelatedLinksDialogOpen(true)}>
                    <Link color="primary" />
                </IconButton>
            </Tooltip>
        }

        {quote && <>
            <PrintReport quote={quote} />
            <DownloadReport quote={quote} />
        </>}

        {userCanModifyQuote &&
            <Tooltip title={tm.Get("Global Option Changes")}>
                <IconButton aria-label="global options" sx={{ padding: 0 }} onClick={handleGlobalOptionsClick}>
                    <GroupWork color="primary" />
                </IconButton>
            </Tooltip>
        }
        {userCanCopyQuote &&
            <Tooltip title={tm.Get("Copy")}>
                <IconButton aria-label="copy" sx={{ padding: 0 }} onClick={handleCopyClick}>
                    <FileCopy color="primary" />
                </IconButton>
            </Tooltip>
        }
        {userCanViewTransLog &&
            <Tooltip title={tm.Get("Transaction Log")}>
                <IconButton aria-label="log" sx={{ padding: 0 }} onClick={handleTransLogClick}>
                    <History color="primary" />
                </IconButton>
            </Tooltip>
        }
        {userCanDeleteQuote &&
            <Tooltip title={tm.Get("Delete")}>
                <IconButton aria-label="delete" sx={{ padding: 0 }} onClick={handleDeleteClick}>
                    <Delete color="primary" />
                </IconButton>
            </Tooltip>
        }
    </>;
};

export default QuoteHeaderActions;
